import { observer } from 'mobx-react';
import React from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ClickArea,
  ClickAreaTrigger,
  Grid,
  GridColumn,
  Heading,
  Icon,
  Tabs,
  TabsItem,
  Text,
  useIsMounted,
} from '@teliaee/design-system';
import UiNotice from '../components/UiNotice';
import { LAST_PANEL_NAME } from '../stores/HomeStore';
import useStores from '../stores/UseStores';
import { useIsMobileHook } from '../util/hooks/useIsMobile';
import { isSmallModuleView } from '../util/ModuleUtil';
import { getStorage } from '../util/StorageUtil';

const PanelSelector = observer((): React.JSX.Element | null => {
  const {
    homeStore: { panels, defaultPanel },
    translateStore: { translate },
    uiStore: { componentWidth, hideNotices, selectedTab, setSelectedTab },
  } = useStores();
  const isMounted = useIsMounted();
  const { isMobile } = useIsMobileHook();

  const handlePanelChange = (panelId: string) => {
    if (isMounted) {
      hideNotices();
      setSelectedTab(panelId);
      getStorage().setItem(LAST_PANEL_NAME, panelId);
    }
  };

  const handleNavigationCardClick = (panelId: string) => {
    setSelectedTab(panelId);
    getStorage().setItem(LAST_PANEL_NAME, panelId);
  };

  const renderTabsOrMobileNavigation = () => {
    if (isMobile) {
      return renderNavigationCards();
    }
    return (
      <Tabs
        aria-labelledby="choose-log-in"
        border="top"
        defaultCurrentTab={defaultPanel}
        align="center"
        currentTab={selectedTab || defaultPanel}
        onTabChange={(_prevTab, nextTab) => handlePanelChange(nextTab)}
      >
        {panels.map((tab) => {
          const ChildComponent = tab.component;
          return (
            <TabsItem id={tab.id} label={translate(`panel.${tab.id}.label`)} key={tab.id} type={tab.employee ? 'employee' : undefined}>
              {selectedTab === tab.id && <UiNotice />}
              <ChildComponent />
            </TabsItem>
          );
        })}
      </Tabs>
    );
  };

  const renderNavigationCards = (): React.JSX.Element => (
    <Grid horizontalGutter="none" verticalGutter="small">
      {panels.map((tab) => {
        const isEmployee = tab.id === 'admin';
        return (
          <GridColumn key={tab.id}>
            <ClickArea>
              <Card type={isEmployee ? 'employee' : 'light'} padding="small" background={isEmployee ? 'employee-bg-subtlest' : 'transparent'}>
                <CardContent>
                  <Grid noWrap align={['middle-xs']} horizontalGutter="small" verticalGutter="none" className={isEmployee ? 'c-employee-text' : undefined}>
                    <GridColumn width={['min']}>
                      <Icon modifiers={tab.icon} size="sm" />
                    </GridColumn>
                    <GridColumn width={['max']}>
                      <Grid noWrap verticalGutter="none" horizontalGutter="small" align={['middle-xs']}>
                        <GridColumn width={['min']}>
                          <Heading level={2} className="h5">
                            <ClickAreaTrigger>
                              <Button
                                text={translate(`panel.${tab.id}.label`)}
                                link
                                isUnderlined={false}
                                type={isEmployee ? 'employee' : 'text-color'}
                                onClick={() => handleNavigationCardClick(tab.id)}
                              />
                            </ClickAreaTrigger>
                          </Heading>
                        </GridColumn>
                        {tab.id === 'passkey' && (
                          <GridColumn width={['max']}>
                            <Badge size="medium" purpose="recommended" text={translate('panel.badge.new')} />
                          </GridColumn>
                        )}
                      </Grid>
                      {tab.id === 'passkey' && (
                        <Text className="c-neutral-text-subtle text-small" element="p">
                          {translate('panel.passkey.subHeading')}
                        </Text>
                      )}
                    </GridColumn>
                    <GridColumn width={['min']}>
                      <Icon modifiers="#arrow-right" size="sm" />
                    </GridColumn>
                  </Grid>
                </CardContent>
              </Card>
            </ClickArea>
          </GridColumn>
        );
      })}
    </Grid>
  );

  if (!panels.length || !isMounted) {
    return null;
  }

  return (
    <>
      <Heading id="choose-log-in" element="h2" className="hidden">
        {translate('panel.chooseLogIn')}
      </Heading>
      {isSmallModuleView(componentWidth) ? renderNavigationCards() : renderTabsOrMobileNavigation()}
    </>
  );
});

export default PanelSelector;
